// Photography App Color System
// Centralized color definitions for consistent theming

export const colors = {
  // Primary Colors - Photography Theme
  primary: {
    main: '#667eea',
    light: '#8fa4f3',
    dark: '#5a6fd8',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    gradientHover: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
  },

  // Secondary Colors
  secondary: {
    main: '#764ba2',
    light: '#9b7bb8',
    dark: '#6a4190'
  },

  // Success Colors
  success: {
    main: '#27ae60',
    light: '#2ecc71',
    dark: '#229954',
    background: 'rgba(39, 174, 96, 0.1)',
    border: '#27ae60'
  },

  // Error Colors
  error: {
    main: '#e74c3c',
    light: '#ff6b6b',
    dark: '#c0392b',
    background: 'rgba(231, 76, 60, 0.1)',
    border: '#e74c3c'
  },

  // Warning Colors
  warning: {
    main: '#f39c12',
    light: '#f1c40f',
    dark: '#e67e22',
    background: 'rgba(243, 156, 18, 0.1)',
    border: '#f39c12'
  },

  // Info Colors
  info: {
    main: '#3498db',
    light: '#5dade2',
    dark: '#2980b9',
    background: 'rgba(52, 152, 219, 0.1)',
    border: '#3498db'
  },

  // Neutral Colors
  neutral: {
    white: '#ffffff',
    black: '#000000',
    transparent: 'transparent'
  },

  // Gray Scale
  gray: {
    50: '#f8f9fa',
    100: '#e9ecef',
    200: '#dee2e6',
    300: '#ced4da',
    400: '#adb5bd',
    500: '#6c757d',
    600: '#495057',
    700: '#343a40',
    800: '#212529',
    900: '#1a1a1a'
  },

  // Text Colors
  text: {
    primary: '#2c3e50',
    secondary: '#7f8c8d',
    muted: '#adb5bd',
    disabled: '#6c757d',
    inverse: '#ffffff'
  },

  // Background Colors
  background: {
    primary: '#ffffff',
    secondary: '#f8f9fa',
    dark: '#2c2c2c',
    overlay: 'rgba(0, 0, 0, 0.4)',
    glass: 'rgba(255, 255, 255, 0.95)',
    glassDark: 'rgba(44, 62, 80, 0.95)'
  },

  // Border Colors
  border: {
    light: '#e9ecef',
    medium: '#ced4da',
    dark: '#adb5bd',
    focus: '#667eea',
    error: '#e74c3c',
    success: '#27ae60'
  },

  // Shadow Colors
  shadow: {
    light: 'rgba(0, 0, 0, 0.05)',
    medium: 'rgba(0, 0, 0, 0.1)',
    dark: 'rgba(0, 0, 0, 0.15)',
    primary: 'rgba(102, 126, 234, 0.3)',
    error: 'rgba(231, 76, 60, 0.3)'
  },

  // Photography Specific Colors
  photography: {
    lens: '#2c3e50',
    aperture: '#34495e',
    shutter: '#7f8c8d',
    film: '#95a5a6',
    vintage: '#d35400',
    sepia: '#8b4513',
    blackAndWhite: '#2c3e50'
  }
};

// Color utility functions
export const colorUtils = {
  // Get color with opacity
  withOpacity: (color: string, opacity: number): string => {
    // Convert hex to rgba
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return color;
  },

  // Get lighter version of color
  lighten: (color: string, amount: number): string => {
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      
      const newR = Math.min(255, r + amount);
      const newG = Math.min(255, g + amount);
      const newB = Math.min(255, b + amount);
      
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }
    return color;
  },

  // Get darker version of color
  darken: (color: string, amount: number): string => {
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      
      const newR = Math.max(0, r - amount);
      const newG = Math.max(0, g - amount);
      const newB = Math.max(0, b - amount);
      
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }
    return color;
  }
};

// CSS Custom Properties for use in CSS
export const cssVariables = {
  '--color-primary': colors.primary.main,
  '--color-primary-light': colors.primary.light,
  '--color-primary-dark': colors.primary.dark,
  '--color-secondary': colors.secondary.main,
  '--color-success': colors.success.main,
  '--color-error': colors.error.main,
  '--color-warning': colors.warning.main,
  '--color-info': colors.info.main,
  '--color-text-primary': colors.text.primary,
  '--color-text-secondary': colors.text.secondary,
  '--color-background-primary': colors.background.primary,
  '--color-background-secondary': colors.background.secondary,
  '--color-border-light': colors.border.light,
  '--color-border-focus': colors.border.focus
};

// Theme object for styled-components or other styling libraries
export const theme = {
  colors,
  colorUtils,
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem'
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    xxl: '20px',
    round: '50%'
  },
  shadows: {
    sm: `0 2px 4px ${colors.shadow.light}`,
    md: `0 4px 8px ${colors.shadow.medium}`,
    lg: `0 8px 16px ${colors.shadow.medium}`,
    xl: `0 16px 32px ${colors.shadow.dark}`,
    primary: `0 4px 12px ${colors.shadow.primary}`
  }
};

export default colors; 